import type {
  CylinderSurface,
  FlatSurface,
  PlacementPosition,
  ProductColorOption,
  ShapeVertex,
  SurfacePoint,
} from '@repo/api-contracts';
import { type ReactElement, useState } from 'react';
import { useStore } from 'zustand';
import {
  compositeSlotsOf,
  cylinderSurfaceFromOutline,
  ellipseHoleRing,
  measurementFrameOf,
  nextAreaName,
  type Rect,
  rectHoleRing,
  replaceCylinderOutline,
  surfaceFromOutline,
  useEditAreaDraftStore,
} from '@/entities/edit-area';
import { FACE_LABELS } from '../model/face-labels';
import { useCylinderPreview } from '../model/use-cylinder-preview';
import { useFullAreaTool } from '../model/use-full-area-tool';
import type { DrawAreaKind } from '../model/use-rect-draw-pointer';
import { useRemoveArea } from '../model/use-remove-area';
import { useSelectArea } from '../model/use-select-area';
import { AreaList } from './area-list';
import { CopyFacePanel } from './copy-face-panel';
import { CylinderPreviewPanel } from './cylinder-preview-panel';
import type { DrawTool } from './draw-tool-bar';
import { EditAreaCanvas } from './edit-area-canvas';
import { PendingInputNotice } from './pending-input-notice';
import { SurfacePanel } from './surface-panel';

interface GeometryWorkspaceProps {
  readonly colors: readonly ProductColorOption[];
}

export const GeometryWorkspace = ({ colors }: GeometryWorkspaceProps): ReactElement => {
  const store = useEditAreaDraftStore();
  const state = useStore(store);
  const selection = state.selection;
  const face = state.faces[selection.face];
  const photo = state.draft.colors.find((color) => color.colorOptionId === selection.colorOptionId)
    ?.faces[selection.face];
  const url = photo ? state.photoUrls[photo.photoKey] : undefined;
  // 원통 미리보기(결정 Q4·R2·Q10)는 면의 초안 원통 표면 하나와 그 면 합성 설정(마스크 키·표시 URL·multiply)으로 만든다.
  // 마스크 키와 표시 URL 은 따로 넘겨 URL 만료를 마스크 없음과 구분한다(결정 D-K). 켜짐 여부만 훅 지역 상태라 초안·저장과 무관하다.
  const onlySurface = face?.surfaces.length === 1 ? face.surfaces[0] : undefined;
  const composite = face?.composite;
  const cylinderPreview = useCylinderPreview({
    surface: onlySurface?.type === 'cylinder' ? onlySurface : undefined,
    photoSize: photo?.photoSize,
    maskKey: composite?.maskKey,
    maskUrl: composite === undefined ? undefined : state.photoUrls[composite.maskKey],
    multiply: composite?.multiply ?? false,
  });
  const fullArea = useFullAreaTool();
  const { select: selectComponent } = useSelectArea();
  // Delete 키 — 사이드바 [인쇄 영역 삭제]와 같은 경로로 이 면에서 지우고 선택을 푼다.
  const deletePlacement = useRemoveArea();
  // [입력 취소]로 사이드바 입력 폼을 저장된 값으로 다시 만들 때 올린다.
  const [inputRevision, setInputRevision] = useState(0);
  // 사이드바 '인쇄 영역' 칸의 도구 줄과 캔버스가 함께 쓰는 도형 도구. 캔버스는 도구 줄을 이 칸에 그린다.
  const [drawTool, setDrawTool] = useState<DrawTool>('select');
  const [toolBarSlot, setToolBarSlot] = useState<HTMLDivElement | null>(null);
  // 사이드바 '표면 설정' 칸 맨 위의 '상품 추출하기' 자리. 추출 상태는 캔버스가 쥐고 버튼만 이 칸에 그린다.
  const [extractSlot, setExtractSlot] = useState<HTMLDivElement | null>(null);
  const select = (change: Partial<typeof selection>): void => {
    if (change.colorOptionId !== undefined) {
      state.setSelection(change);
      return;
    }
    if (change.face === selection.face) return;
    const pending = Object.keys(state.pendingInputs).filter((key) => key !== 'component');
    if (
      pending.length > 0 &&
      !window.confirm('적용하지 않은 표면·배치 입력을 버리고 선택을 바꾸시겠습니까?')
    )
      return;
    for (const key of pending) state.setInputDirty(key, false);
    state.setSelection(change);
  };
  /**
   * '상품 추출하기'로 닫은 윤곽의 표면(결정 Q2). 면에 표면이 있으면 같은 ID 로 교체한다 — 기존 배치의 surfaceId 가
   * 끊기지 않는다. 원통 상품은 원통 표면을 만들고, 이미 원통 표면이 있으면 윤곽만 바꿔 사각형·곡률·합성 설정을 둔다.
   * 윤곽 교체는 실측을 쓰지 않으므로 상품 실측이 없어도 된다. 새 표면은 실측이 있어야 한다 — 표면 실측을 채울 값이 없다.
   * 평면 표면의 틀은 사진 px 비율로 실측 비율에 맞추므로 사진 크기도 있어야 한다(캔버스가 사진 없는 추출을 막는다).
   */
  const extractedSurface = (points: SurfacePoint[]): FlatSurface | CylinderSurface | null => {
    if (face === undefined) return null;
    const existing = face.surfaces[0];
    if (state.measurementKind === 'CYLINDER' && existing?.type === 'cylinder')
      return replaceCylinderOutline(existing, points);
    if (state.measurement === null) return null;
    const id = existing?.id ?? crypto.randomUUID();
    if (state.measurementKind === 'FLAT')
      return photo === undefined
        ? null
        : surfaceFromOutline(id, state.measurement, points, photo.photoSize);
    return cylinderSurfaceFromOutline(
      id,
      measurementFrameOf(state.measurementKind, state.measurement),
      points,
    );
  };
  /**
   * 캔버스에서 닫은 인쇄 영역 점열(표면 mm)로 다각형 영역과 그 면 배치를 만든다. 거부되면 사유를 돌려줘 캔버스가 점을
   * 남긴 채 보인다. 새 영역이면 만든 뒤 이름으로 찾아 선택한다(이름은 겹치지 않는다).
   */
  const closeShape = (points: PlacementPosition[]): string | null => {
    const drawing = state.shapeDrawing;
    if (drawing === null) return null;
    const surface = face?.surfaces[0];
    if (surface === undefined) return '표면을 먼저 추출해 주세요.';
    if (drawing.mode === 'hole') {
      if (drawing.componentId === null)
        return '인쇄 제외 영역을 지정할 인쇄 영역을 찾을 수 없습니다.';
      return addHole(drawing.componentId, points);
    }
    const applied = state.applyDrawnShape(selection.face, {
      componentId: drawing.componentId,
      name: drawing.name,
      surfaceId: surface.id,
      points,
    });
    if (!applied) return store.getState().commandError ?? '인쇄 영역을 만들 수 없습니다.';
    const name = drawing.name.trim();
    const componentId =
      drawing.componentId ??
      store.getState().components.find((component) => component.name === name)?.id ??
      null;
    state.endShapeDrawing();
    state.setSelection({ componentId });
    return null;
  };
  /**
   * 그리는 중인 영역에 구멍(표면 mm 점열)을 하나 더하고 그리기를 끝낸다 — [+] 한 번에 구멍 하나다. 더한 구멍은 스토어가
   * 고른다. 거부되면 사유를 돌려준다(그리기는 그대로라 다시 그릴 수 있다).
   */
  const addHole = (componentId: string, points: ShapeVertex[]): string | null => {
    const surface = face?.surfaces[0];
    if (surface === undefined) return '표면을 먼저 추출해 주세요.';
    const applied = state.applyDrawnHole(selection.face, {
      componentId,
      surfaceId: surface.id,
      points,
    });
    if (!applied) return store.getState().commandError ?? '인쇄 제외 영역을 만들 수 없습니다.';
    state.endShapeDrawing();
    return null;
  };
  /** 인쇄 제외 영역 사각형·원형 도구로 끌어 놓은 사각형 — 사각형은 네 모서리, 원형은 부드러운 점 넷 구멍으로 더한다. */
  const drawHole = (kind: DrawAreaKind, rect: Rect): string | null => {
    const drawing = state.shapeDrawing;
    if (drawing?.mode !== 'hole' || drawing.componentId === null)
      return '인쇄 제외 영역을 지정할 인쇄 영역을 찾을 수 없습니다.';
    return addHole(
      drawing.componentId,
      kind === 'rect' ? rectHoleRing(rect) : ellipseHoleRing(rect),
    );
  };
  /** 끌거나 화살표 키로 옮긴 구멍. 거부되면 사유를 돌려준다(구멍은 그대로). */
  const moveHole = (componentId: string, index: number, dxMm: number, dyMm: number) =>
    state.moveShapeHole(selection.face, componentId, index, dxMm, dyMm)
      ? null
      : (store.getState().commandError ?? '인쇄 제외 영역을 옮길 수 없습니다.');
  /** Delete 키로 지운 구멍. 거부되면 사유를 돌려준다. */
  const deleteHole = (componentId: string, index: number): string | null =>
    state.removeShapeHole(componentId, index)
      ? null
      : (store.getState().commandError ?? '인쇄 제외 영역을 지울 수 없습니다.');
  /** 사각형·원형 도구로 끌어 놓은 사각형으로 영역과 이 면 배치를 만들고 선택한다. 거부되면 사유를 돌려준다. */
  const drawArea = (kind: DrawAreaKind, rect: Rect): string | null => {
    const surface = face?.surfaces[0];
    if (surface === undefined) return '표면을 먼저 추출해 주세요.';
    const componentId = state.createDrawnArea(selection.face, {
      kind,
      surfaceId: surface.id,
      rect,
    });
    if (componentId === null)
      return store.getState().commandError ?? '인쇄 영역을 만들 수 없습니다.';
    state.setSelection({ componentId });
    return null;
  };
  /** 원통 표면에서 사각형·원형 도구를 누르면 표면 가운데에 기본 크기로 만들고 선택한다. 거부되면 사유를 돌려준다. */
  const createDefaultArea = (kind: DrawAreaKind): string | null => {
    const componentId = state.createDefaultArea(selection.face, kind);
    if (componentId === null)
      return store.getState().commandError ?? '인쇄 영역을 만들 수 없습니다.';
    state.setSelection({ componentId });
    return null;
  };
  /** 핸들로 끌어 바꾼 크기로 영역과 이 면 배치를 바꾼다. 거부되면 사유를 돌려준다(원래 크기 그대로). */
  const resizePlacement = (componentId: string, rect: Rect): string | null =>
    state.resizeArea(selection.face, componentId, rect)
      ? null
      : (store.getState().commandError ?? '인쇄 영역 크기를 바꿀 수 없습니다.');
  /** 점 편집으로 고친 바깥 고리 점(표면 mm)으로 모양과 이 면 배치를 바꾼다. 거부되면 사유를 돌려준다(모양은 그대로). */
  const editShapePoints = (componentId: string, points: ShapeVertex[]): string | null =>
    state.editShapePoints(selection.face, componentId, points)
      ? null
      : (store.getState().commandError ?? '인쇄 영역 모양을 바꿀 수 없습니다.');
  /** 구멍 점 편집으로 고친 구멍 점(표면 mm)으로 그 구멍을 바꾼다. 거부되면 사유를 돌려준다(구멍은 그대로). */
  const editHolePoints = (componentId: string, index: number, points: ShapeVertex[]) =>
    state.editHolePoints(selection.face, componentId, index, points)
      ? null
      : (store.getState().commandError ?? '인쇄 제외 영역 모양을 바꿀 수 없습니다.');
  /** 화살표 키로 옮긴 위치로 이 면 배치를 바꾼다. 거부되면 사유를 돌려준다(위치는 그대로). */
  const nudgePlacement = (componentId: string, position: PlacementPosition): string | null => {
    const placement = face?.placements.find((item) => item.componentId === componentId);
    if (placement === undefined) return '배치를 찾을 수 없습니다.';
    return state.upsertPlacement(selection.face, { ...placement, position })
      ? null
      : (store.getState().commandError ?? '인쇄 영역을 옮길 수 없습니다.');
  };
  /**
   * 표면 설정 칸의 [표면 삭제] — 이 표면에 놓인 배치나 이 면의 합성 설정(마스크·음영·안내선, 진행 중인 합성 업로드 포함)이 있으면
   * 함께 지운다고 확인받고, 둘 다 없으면 바로 지운다. 지우면 표면 선택을 푼다.
   */
  const deleteSurface = (surfaceId: string): void => {
    const count =
      face?.placements.filter((placement) => placement.surfaceId === surfaceId).length ?? 0;
    const composite =
      face?.composite !== undefined ||
      compositeSlotsOf(state.operations, selection.face).length > 0;
    const message =
      count > 0 && composite
        ? `이 표면에 놓인 인쇄 영역 배치 ${count}개와 이 면의 마스크·음영·안내선도 함께 삭제됩니다. 삭제하시겠습니까?`
        : count > 0
          ? `이 표면에 놓인 인쇄 영역 배치 ${count}개도 함께 삭제됩니다. 삭제하시겠습니까?`
          : composite
            ? '이 면의 마스크·음영·안내선도 함께 삭제됩니다. 삭제하시겠습니까?'
            : null;
    if (message !== null && !window.confirm(message)) return;
    if (state.removeSurface(selection.face, surfaceId)) state.setSelection({ surfaceId: null });
  };
  // 인쇄 영역 폼(이름·가로·세로) 입력은 캔버스를 잠그지 않는다 — 칸을 떠나면 바로 적용되므로 사진을 누르면 먼저 적용된다.
  // 표면 수치 입력은 고치는 동안 잠근다(칸을 떠나거나 Enter 로 적용, Esc·[입력 취소]로 되돌린다).
  const pendingPointer = Object.keys(state.pendingInputs).some(
    (key) => key !== 'component' && (key !== 'surface' || Boolean(selection.surfaceId)),
  );
  // 편집 잠금(저장 중·지원하지 않는 정의)이 아니고 입력 때문에만 잠겼을 때 입력 안내를 보인다.
  const inputsPending = pendingPointer && !state.locked && !state.isUnsupported;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-4">
        <label className="grid gap-1 text-sm">
          기준 사진 색상
          <select
            className="rounded border bg-background p-2"
            value={selection.colorOptionId ?? ''}
            disabled={state.locked}
            onChange={(event) => select({ colorOptionId: event.target.value })}
          >
            {colors.map((color) => (
              <option key={color.id} value={color.id}>
                {color.name}
                {color.isAvailable ? '' : ' (공급 불가)'}
              </option>
            ))}
          </select>
        </label>
        <label className="grid gap-1 text-sm">
          편집할 면
          <select
            className="rounded border bg-background p-2"
            value={selection.face}
            disabled={state.locked}
            onChange={(event) => {
              const next = state.draft.activeFaces.find((key) => key === event.target.value);
              if (next) select({ face: next, surfaceId: null });
            }}
          >
            {state.draft.activeFaces.map((key) => (
              <option key={key} value={key}>
                {FACE_LABELS[key]}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_320px]">
        {face ? (
          <div className="min-w-0 space-y-2 xl:sticky xl:top-4 xl:self-start">
            {inputsPending ? (
              <PendingInputNotice
                onCancel={() => {
                  state.clearPendingInputs();
                  setInputRevision((value) => value + 1);
                }}
                onCancelDrawing={state.shapeDrawing === null ? undefined : state.endShapeDrawing}
              />
            ) : null}
            <EditAreaCanvas
              key={`${selection.colorOptionId}:${selection.face}:${state.generation}`}
              photo={photo && url ? { ...photo, photoUrl: url } : undefined}
              components={state.components}
              faceDefinition={face}
              selectedSurfaceId={selection.surfaceId}
              disabled={state.locked || state.isUnsupported || pendingPointer}
              inputsPending={inputsPending}
              measurement={state.measurement}
              cylinderPreview={cylinderPreview.overlay}
              shapeDrawing={state.shapeDrawing}
              onShapeClose={closeShape}
              onShapeCancel={state.endShapeDrawing}
              onBeginPolygon={() => {
                state.beginShapeDrawing({
                  componentId: null,
                  name: nextAreaName(
                    '다각형',
                    state.components.map((component) => component.name),
                  ),
                });
              }}
              onDrawArea={drawArea}
              onCreateDefaultArea={createDefaultArea}
              onDrawHole={drawHole}
              selectedHoleIndex={selection.holeIndex}
              onSelectHole={(holeIndex) => state.setSelection({ holeIndex })}
              onMoveHole={moveHole}
              onDeleteHole={deleteHole}
              fullArea={fullArea}
              selectedComponentId={selection.componentId}
              onSelectComponent={selectComponent}
              onPlacementResize={resizePlacement}
              onEditShapePoints={editShapePoints}
              onEditHolePoints={editHolePoints}
              onDeletePlacement={deletePlacement}
              onPlacementNudge={nudgePlacement}
              drawTool={drawTool}
              onDrawToolChange={setDrawTool}
              toolBarContainer={toolBarSlot}
              extractContainer={extractSlot}
              onSurfaceExtract={(points) => {
                const surface = extractedSurface(points);
                if (surface !== null && state.upsertSurface(selection.face, surface)) {
                  state.setInputDirty('surface', false);
                  state.setSelection({ surfaceId: surface.id });
                }
              }}
              onPlacementMove={(componentId, position) => {
                const placement = face.placements.find((item) => item.componentId === componentId);
                if (placement) state.upsertPlacement(selection.face, { ...placement, position });
              }}
            />
          </div>
        ) : null}
        <div className="space-y-4">
          {/* 작업 순서대로 표면 설정 → 다른 면과 똑같이 맞추기(표면·배치 가져오기) → 인쇄 영역 → 원통 미리보기. */}
          <SurfacePanel
            key={`surface:${selection.face}:${state.generation}:${inputRevision}`}
            extractSlotRef={setExtractSlot}
            onDeleteSurface={deleteSurface}
          />
          <CopyFacePanel key={`copy:${selection.face}:${state.generation}`} />
          <section aria-label="인쇄 영역" className="space-y-3 rounded-lg border p-3">
            <h2 className="font-semibold">인쇄 영역</h2>
            <div ref={setToolBarSlot} />
            <AreaList key={`areas:${state.generation}:${inputRevision}`} />
          </section>
          {state.measurementKind === 'CYLINDER' ? (
            <CylinderPreviewPanel control={cylinderPreview} />
          ) : null}
        </div>
      </div>
    </div>
  );
};
